/**
 * Estimated walk/drive time from the venue to a restaurant — the fallback
 * for when Routes API isn't configured (see `hooks/useTravelTime.js`, which
 * prefers the real server-computed value and only falls back to this).
 * Straight-line distance, inflated by a fixed factor for how much longer
 * real streets are than a straight line, divided by an assumed average
 * speed. Good enough for "is this walkable," not for turn-by-turn.
 */

const EARTH_RADIUS_MILES = 3958.8;
const ROAD_DETOUR_FACTOR = 1.3; // streets aren't straight lines
const WALK_SPEED_MPH = 3;
const DRIVE_SPEED_MPH = 18; // city driving — lights and stops included
const WALK_LIMIT_MINUTES = 30;

/**
 * @param {[number, number]} from
 * @param {[number, number]} to
 * @returns {number} straight-line miles
 */
export function haversineMiles([lat1, lng1], [lat2, lng2]) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_MILES * 2 * Math.asin(Math.sqrt(a));
}

/**
 * @param {[number, number]} from
 * @param {[number, number]} to
 * @returns {{walkMinutes: number, driveMinutes: number}}
 */
export function estimateTravelTime(from, to) {
  const roadMiles = haversineMiles(from, to) * ROAD_DETOUR_FACTOR;
  return {
    walkMinutes: Math.max(1, Math.round((roadMiles / WALK_SPEED_MPH) * 60)),
    driveMinutes: Math.max(1, Math.round((roadMiles / DRIVE_SPEED_MPH) * 60)),
  };
}

/**
 * Walking time only shows up when it's actually a reasonable option; past
 * that, driving is the only distance that matters. Shared by both the real
 * (Routes API) and estimated times — same rule either way.
 *
 * @param {{walkMinutes: number, driveMinutes: number}} times
 * @returns {string} e.g. `"8 min walk · 15 min drive"` or `"22 min drive"`
 */
export function formatTravelLabel({ walkMinutes, driveMinutes }) {
  return walkMinutes <= WALK_LIMIT_MINUTES
    ? `${walkMinutes} min walk · ${driveMinutes} min drive`
    : `${driveMinutes} min drive`;
}

/**
 * Which mode `formatTravelLabel` would lead with — used to default the
 * directions chooser to the same mode already shown on the card.
 *
 * @param {{walkMinutes: number, driveMinutes: number}} times
 * @returns {'walking'|'driving'}
 */
export function preferredTravelMode({ walkMinutes }) {
  return walkMinutes <= WALK_LIMIT_MINUTES ? 'walking' : 'driving';
}
